import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  apiGetAllUsers, apiGetPendingUsers, apiApproveUser, apiRejectUser,
  apiGetUserBalances, apiSetUserBalance,
  apiExtendSubscription, apiPauseSubscription, apiResumeSubscription,
  apiCancelSubscription, apiDeleteUser,
  apiTriggerDigest, apiGetCacheStats, apiOptionQuoteDebug, apiOptionContractsList, apiGetVixRegime,
  apiGetCalibrationCurve, apiRunCalibrationCurve, apiGetOutcomesHealth,
  apiGetShortFlow, apiRunShortFlowJob, apiGetInsiderFlow, apiRunInsiderFlowJob,
  apiGetTrackedSignals, apiGetPerformanceTrend,
  apiGetFactorAttribution, apiRunFactorAttribution,
} from '../utils/backendApi';
import { Shield, Users, RefreshCw, DollarSign, Clock, CheckCircle, PauseCircle, XCircle, Infinity, Trash2, Play, Mail, Database, Search, Target, Activity } from 'lucide-react';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Tabs from '../components/ui/Tabs';
import TrackRecordCard from '../components/TrackRecordCard';
import { useToast } from '../components/ui/Toast';

// ── Helpers ────────────────────────────────────────────────────────────────

function getEffectiveStatus(user) {
  const s = user.subscription_status;
  if (s === 'trial' && (!user.trial_ends_at || new Date(user.trial_ends_at) <= new Date())) return 'expired';
  if (s === 'active' && user.subscription_ends_at && new Date(user.subscription_ends_at) <= new Date()) return 'expired';
  return s;
}

const STATUS_CONFIG = {
  trial:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: Clock,         label: 'Trial' },
  active:    { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  icon: CheckCircle,   label: 'Active' },
  paused:    { color: '#f97316', bg: 'rgba(249,115,22,0.12)',  icon: PauseCircle,   label: 'Paused' },
  cancelled: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: XCircle,       label: 'Cancelled' },
  expired:   { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: XCircle,       label: 'Expired' },
};

// Map STATUS_CONFIG semantics to the shared Badge primitive's tone vocabulary.
const STATUS_TONE = {
  trial:     'amber',
  active:    'green',
  paused:    'amber',
  cancelled: 'red',
  expired:   'red',
};
function StatusBadge({ user }) {
  const s = getEffectiveStatus(user);
  const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.expired;
  return (
    <Badge tone={STATUS_TONE[s] || 'red'} size="sm" icon={cfg.icon}>
      {cfg.label}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
}

// ── Main Component ─────────────────────────────────────────────────────────

function DigestTrigger({ onMessage }) {
  const [busy, setBusy] = useState(false);
  const fire = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiTriggerDigest();
      const note = res.signals
        ? `Digest sent · ${res.signals} signals · ${res.sent}/${res.total} emails delivered`
        : res.message || 'Digest triggered';
      onMessage(note, true);
    } catch (e) {
      onMessage(`Failed to trigger digest: ${e.message}`, false);
    }
    setBusy(false);
  };
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)', flex: '1 1 0', minWidth: 260 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Mail size={14} color="var(--accent-soft)" />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Daily digest</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Manually re-send the morning digest email to all approved users. Skips the schedule; respects the same signal-quality filters and email templates.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="secondary" size="sm" icon={busy ? RefreshCw : Play} loading={busy} onClick={fire}>
          {busy ? 'Sending…' : 'Send digest now'}
        </Button>
      </div>
    </div>
  );
}

function CacheStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiGetCacheStats());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const caches = data ? Object.values(data.caches) : [];
  const summary = data?.summary;

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Database size={14} color="var(--green)" />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Server cache health</span>
          {summary && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · {summary.hitRatePct}% hit rate · {summary.coalesced} coalesced
            </span>
          )}
        </span>
        <Button variant="ghost" size="xs" icon={RefreshCw} loading={loading} onClick={load}>
          Refresh
        </Button>
      </div>
      {error && (
        <div style={{ fontSize: 11, color: 'var(--red-soft)', marginBottom: 8 }}>Error: {error}</div>
      )}
      {!error && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 540 }}>
            <thead>
              <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Cache</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Size</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Hits</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Misses</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Coalesced</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Evictions</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Hit rate</th>
              </tr>
            </thead>
            <tbody>
              {caches.map(c => {
                const rateColor = c.hitRatePct >= 70 ? 'var(--green)' : c.hitRatePct >= 40 ? 'var(--amber)' : 'var(--red)';
                return (
                  <tr key={c.name}>
                    <td style={{ padding: '6px 8px', color: 'var(--text-primary)', fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{c.size}/{c.maxSize}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--green)' }}>{c.hits.toLocaleString()}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{c.misses.toLocaleString()}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--accent-soft)' }}>{c.coalesced.toLocaleString()}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{c.evictions.toLocaleString()}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: rateColor, fontWeight: 600 }}>{c.hitRatePct}%</td>
                  </tr>
                );
              })}
              {caches.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', color: 'var(--text-faint)' }}>No caches registered yet — fire a request to populate.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Live VIX level + classified regime + active gate floors. Polls every 60s.
// The regime banner on the Signals page shows this same data inline; this
// panel is the canonical "what gates are firing right now" view.
function VixRegimePanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setData(await apiGetVixRegime()); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  const regime = data?.vixRegime;
  const color  = regime === 'stressed' ? 'var(--amber)'
              : regime === 'calm'     ? 'var(--green)'
              : regime === 'normal'   ? 'var(--text-secondary)'
              : 'var(--text-faint)';

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>VIX volatility regime</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· auto-refreshes every 60s</span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" size="xs" loading={loading} onClick={load}>Refresh</Button>
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--red-soft)', marginBottom: 8 }}>Error: {error}</div>}
      {data && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-secondary)' }}>
          {[
            { label: 'VIX level',         value: data.vixLevel != null ? data.vixLevel.toFixed(2) : '—',          tone: 'var(--text-primary)' },
            { label: 'Regime',            value: regime || '—',                                                    tone: color },
            { label: 'Score floor',       value: `≥ ${data.gates?.minScore ?? '—'}`,                              tone: 'var(--text-primary)' },
            { label: 'Confidence floor',  value: `≥ ${data.gates?.minConfidence ?? '—'}%`,                        tone: 'var(--text-primary)' },
            { label: 'Counter-trend LONG', value: `score ≥ ${data.gates?.longCounterTrendScoreMin ?? '—'}`,        tone: 'var(--text-primary)' },
          ].map(m => (
            <div key={m.label} style={{
              flex: '1 1 130px', minWidth: 0,
              background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
              padding: '10px 12px', border: '1px solid var(--border-subtle)',
            }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4, fontWeight: 600 }}>{m.label}</div>
              <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: m.tone }}>{m.value}</div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'flex-end', flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* T3.2: which classifier is active, and what bands it used. */}
            <div>
              Classifier:{' '}
              <span className="mono" style={{ color: data.source?.startsWith('percentile') ? 'var(--accent-soft)' : 'var(--text-secondary)' }}>
                {data.source || 'fixed'}
              </span>
              {data.flagEnabled === false && <span style={{ color: 'var(--text-muted)' }}> · SIGNAL_V2_VIX_PERCENTILE off</span>}
            </div>
            <div>
              Fixed bands: VIX &lt;{data.bands?.CALM_BELOW} = calm · {data.bands?.CALM_BELOW}–{data.bands?.STRESSED_AT} = normal · ≥{data.bands?.STRESSED_AT} = stressed
            </div>
            {data.percentileBands && (
              <div>
                Percentile bands: ≤ {data.percentileBands.CALM_BELOW}th = calm · ≥ {data.percentileBands.STRESSED_ABOVE}th = stressed
                {data.vixHistory && <> · trailing window n={data.vixHistory.n}, range {data.vixHistory.min?.toFixed?.(1)}–{data.vixHistory.max?.toFixed?.(1)}</>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Diagnostic for the "no live bid/ask" issue. Takes an underlying + contract
// symbol, hits /api/admin/option-quote-debug, and renders the flat summary
// from Polygon's snapshot. The raw body is also shown so we can pin down
// whether bid/ask are missing upstream or being mis-parsed downstream.
function OptionQuoteInspector() {
  const [underlying, setUnderlying] = useState('');
  const [contract, setContract] = useState('');
  const [data, setData] = useState(null);
  const [listData, setListData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!underlying.trim() || !contract.trim()) {
      setError('Both underlying and contract are required (e.g. SMU, O:SMU260617P00013000)');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const out = await apiOptionQuoteDebug(underlying.trim().toUpperCase(), contract.trim());
      setData(out);
    } catch (e2) {
      setError(e2.message);
    }
    setLoading(false);
  };

  const runList = async () => {
    if (!underlying.trim()) {
      setError('Underlying is required to list contracts');
      return;
    }
    setLoadingList(true);
    setError(null);
    setListData(null);
    try {
      const out = await apiOptionContractsList(underlying.trim().toUpperCase(), 200);
      setListData(out);
    } catch (e2) {
      setError(e2.message);
    }
    setLoadingList(false);
  };

  const sum = data?.summary;
  const renderTimeframe = sum?.last_quote_timeframe;
  const tfColor = renderTimeframe === 'REAL-TIME' ? 'var(--green)'
                : renderTimeframe === 'DELAYED'   ? 'var(--amber)'
                : 'var(--text-secondary)';
  const quoteAgeMs = sum?.last_quote_last_updated
    ? Math.max(0, Date.now() - Math.floor(sum.last_quote_last_updated / 1_000_000))
    : null;
  const fmtAge = (ms) => {
    if (ms == null) return '—';
    const m = Math.floor(ms / 60000);
    if (m < 1)  return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m ago`;
    return `${Math.floor(h / 24)}d ${h % 24}h ago`;
  };

  const inputStyle = {
    padding: '7px 12px', borderRadius: 'var(--r-sm)',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit', outline: 'none',
    transition: `border-color var(--dur-fast) var(--ease-out)`,
  };

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Search size={14} color="var(--accent-soft)" />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Polygon option quote — raw inspector</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· hits /v3/snapshot/options/&lt;u&gt;/&lt;c&gt; directly</span>
      </div>

      <form onSubmit={run} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <input
          value={underlying}
          onChange={(e) => setUnderlying(e.target.value)}
          placeholder="Underlying — SMU"
          style={{ ...inputStyle, flex: '1 1 120px' }}
        />
        <input
          value={contract}
          onChange={(e) => setContract(e.target.value)}
          placeholder="Contract — O:SMU260617P00013000"
          className="mono"
          style={{ ...inputStyle, flex: '2 1 280px' }}
        />
        <Button type="submit" variant="primary" size="sm" loading={loading}>
          {loading ? 'Fetching…' : 'Fetch quote'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={loadingList}
          onClick={runList}
          title="Lists every contract Polygon has for this underlying."
        >
          {loadingList ? 'Loading…' : 'List contracts'}
        </Button>
      </form>

      {error && (
        <div style={{
          fontSize: 11, color: 'var(--red-soft)', marginBottom: 8,
          padding: '8px 10px', background: 'var(--red-dim)',
          borderRadius: 'var(--r-sm)', border: '1px solid rgba(239,68,68,0.20)',
        }}>
          {error}
        </div>
      )}

      {sum && (
        <div style={{ marginBottom: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 360 }}>
            <tbody>
              {[
                ['Upstream HTTP',       data.upstreamStatus,                                                         data.upstreamStatus === 200 ? 'var(--green)' : 'var(--red)'],
                ['Polygon status',      data.polygonStatus ?? '—',                                                    'var(--text-primary)'],
                ['last_quote present?', String(!!sum.last_quote_present),                                             sum.last_quote_present ? 'var(--green)' : 'var(--red)'],
                ['last_quote.timeframe', renderTimeframe ?? '—',                                                       tfColor],
                ['bid / ask',           `${sum.last_quote_bid ?? 'null'} / ${sum.last_quote_ask ?? 'null'}`,           'var(--text-primary)'],
                ['bid_size / ask_size', `${sum.last_quote_bid_size ?? 'null'} / ${sum.last_quote_ask_size ?? 'null'}`, 'var(--text-primary)'],
                ['quote last updated',  fmtAge(quoteAgeMs),                                                            'var(--text-primary)'],
                ['last_trade.price',    sum.last_trade_price ?? 'null',                                                'var(--text-primary)'],
                ['day.close / volume',  `${sum.day_close ?? 'null'} / ${sum.day_volume ?? 'null'}`,                    'var(--text-primary)'],
                ['implied volatility',  sum.implied_volatility != null ? `${(sum.implied_volatility * 100).toFixed(1)}%` : 'null', 'var(--text-primary)'],
              ].map(([label, val, valColor]) => (
                <tr key={label}>
                  <td style={{ padding: '5px 8px', color: 'var(--text-muted)' }}>{label}</td>
                  <td style={{ padding: '5px 8px', color: valColor, fontWeight: 600 }}>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <details style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          <summary style={{ cursor: 'pointer', padding: '4px 0', color: 'var(--text-secondary)' }}>Raw Polygon body</summary>
          <pre className="mono" style={{
            background: 'var(--bg-base)', padding: 10, borderRadius: 'var(--r-sm)',
            color: 'var(--text-secondary)', fontSize: 10, overflow: 'auto', maxHeight: 360, marginTop: 6,
            border: '1px solid var(--border-subtle)',
          }}>{JSON.stringify(data.rawBody, null, 2)}</pre>
        </details>
      )}

      {listData && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
              Polygon contracts for {underlying.toUpperCase()}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · {listData.count} returned
              {listData.polygonNextUrl ? ' (more pages available — raise the limit)' : ''}
            </span>
          </div>
          {listData.count === 0 ? (
            <div style={{
              fontSize: 12, color: 'var(--amber)', padding: '8px 10px',
              background: 'var(--amber-dim)', borderRadius: 'var(--r-sm)',
              border: '1px solid rgba(245,158,11,0.20)',
            }}>
              ⚠ Polygon has no listed contracts for {underlying.toUpperCase()}.
              The signal generator should never have produced an option play for this
              underlying — that's a bug in findBestOption / the chain endpoint.
            </div>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: 360, WebkitOverflowScrolling: 'touch' }}>
              <table className="mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 560 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
                  <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Ticker</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Type</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>Strike</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Expiry</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>Sh/contract</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {listData.contracts.map((c) => {
                    const match = c.ticker === contract.trim();
                    const oddShares = c.shares_per_contract && c.shares_per_contract !== 100;
                    return (
                      <tr key={c.ticker} style={{ background: match ? 'var(--green-dim)' : 'transparent' }}>
                        <td style={{ padding: '4px 8px', color: match ? 'var(--green)' : 'var(--text-primary)' }}>
                          {match ? '✓ ' : ''}{c.ticker}
                        </td>
                        <td style={{ padding: '4px 8px', color: c.contract_type === 'call' ? 'var(--green)' : 'var(--red)' }}>{c.contract_type}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--text-primary)' }}>${c.strike_price}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--text-secondary)' }}>{c.expiration_date}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: oddShares ? 'var(--amber)' : 'var(--text-secondary)' }}>{c.shares_per_contract ?? '—'}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--text-muted)' }}>
                          {c.additional_underlyings ? '⚠ adjusted' : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── T1.1 / T1.2: confidence reliability curve ─────────────────────────────
// Visualises confidence_calibration_curve. Per 5-pt predicted bin we show
// raw observed WR, PAVA-monotone pooled WR (what the UI displays), Wilson
// 95% CI on the pool, and whether the bin is identity-mapped (still
// building track record).
//
// IMPORTANT: this panel does NOT load on mount. The user clicks "Load" to
// fetch — keeps admin-page navigation cheap and prevents the kind of
// mount-stampede that triggered the Phase-1 prod-slowness rollback.
function CalibrationCurvePanel({ onMessage }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error,   setError]   = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await apiGetCalibrationCurve()); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const rebuild = async () => {
    if (running) return;
    setRunning(true);
    try {
      const res = await apiRunCalibrationCurve();
      onMessage?.(`Curve rebuilt — ${res.stats?.outcomes ?? 0} outcomes → ${res.stats?.calibrated ?? 0} calibrated bins`, true);
      setData(res);
    } catch (e) { onMessage?.(`Calibration rebuild failed: ${e.message}`, false); }
    setRunning(false);
  };

  const curve = data?.curve || [];
  const calibratedBins = curve.filter(b => !b.identity_mapped && b.raw_n > 0).length;
  const identityBins   = curve.filter(b =>  b.identity_mapped && b.raw_n > 0).length;
  const totalSamples   = curve.reduce((s, b) => s + (Number(b.raw_n) || 0), 0);
  const lastBuilt      = curve[0]?.computed_at;
  const loaded         = data !== null;

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Target size={14} color="var(--accent-soft)" />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Confidence reliability curve</span>
          {loaded && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · {calibratedBins} calibrated · {identityBins} identity-mapped · n={totalSamples}
            </span>
          )}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="ghost"     size="xs" icon={RefreshCw} loading={loading} onClick={load}>{loaded ? 'Refresh' : 'Load'}</Button>
          <Button variant="secondary" size="xs" icon={Play}      loading={running} onClick={rebuild}>Rebuild now</Button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
        Rebuilds from the trailing 180 days of scored outcomes. Each predicted-confidence bin is mapped to the historically observed win rate at that score, so a signal shown at "82%" actually means "signals at this level have profited 82% of the time." Bins with too few samples stay identity-mapped — the UI displays the raw confidence until enough data accumulates. Click <b>Load</b> to fetch the current snapshot, <b>Rebuild now</b> to regenerate from latest outcomes.
        {lastBuilt && <> · Last built {new Date(lastBuilt).toLocaleString()}</>}
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--red-soft)', marginBottom: 8 }}>Error: {error}</div>}
      {loaded && !error && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 540 }}>
            <thead>
              <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Bin</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Raw n</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Raw WR</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Pooled n</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Mapped WR</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>95% CI</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {curve.map(b => {
                const identity = !!b.identity_mapped;
                const rawN     = Number(b.raw_n) || 0;
                const mapped   = b.observed_wr_pct == null ? null : Number(b.observed_wr_pct);
                const rawWr    = Number(b.raw_observed_wr_pct) || 0;
                const mappedColor = mapped == null ? 'var(--text-faint)'
                                   : mapped >= 60 ? 'var(--green)'
                                   : mapped >= 45 ? 'var(--amber)'
                                                  : 'var(--red)';
                return (
                  <tr key={`${b.bin_low}-${b.bin_high}`} style={{ opacity: rawN === 0 ? 0.45 : 1 }}>
                    <td style={{ padding: '6px 8px', color: 'var(--text-primary)', fontWeight: 500 }}>
                      {b.bin_low}–{b.bin_high}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{rawN}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{rawN > 0 ? `${rawWr.toFixed(1)}%` : '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--accent-soft)' }}>{b.n || 0}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: mappedColor, fontWeight: 600 }}>
                      {mapped == null ? '—' : `${mapped.toFixed(1)}%`}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-muted)' }}>
                      {b.ci_low == null ? '—' : `${Number(b.ci_low).toFixed(0)}–${Number(b.ci_high).toFixed(0)}%`}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {rawN === 0
                        ? <Badge tone="neutral" size="sm">empty</Badge>
                        : identity
                          ? <Badge tone="amber" size="sm">identity</Badge>
                          : <Badge tone="green" size="sm">calibrated</Badge>}
                    </td>
                  </tr>
                );
              })}
              {curve.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', color: 'var(--text-faint)' }}>
                  Curve not built yet — click "Rebuild now" once outcomes have been scored.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── T2.1: outcome-flywheel health ─────────────────────────────────────────
// Per-exchange view of signal_outcomes: total / scored / pending, shipped vs
// shadow split, and 7-day activity. The shipped vs shadow ratio is the most
// useful number — confirms the digest is persisting both buckets. Manual
// load (no useEffect) for the same reason CalibrationCurvePanel skips it.
function OutcomesHealthPanel() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await apiGetOutcomesHealth()); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const rows = data?.exchanges || [];

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={14} color="var(--green)" />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Outcome flywheel health</span>
        </span>
        <Button variant="ghost" size="xs" icon={RefreshCw} loading={loading} onClick={load}>{data !== null ? 'Refresh' : 'Load'}</Button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
        Confirms the Phase-2 flywheel is on. <b>Shipped</b> = signals emailed to users (top-5 picks). <b>Shadow</b> = gate-passing candidates that didn't make top-5 but are tracked anyway to feed the calibration curve + walk-forward weights with ~10× more samples. The headline track record (`/api/signals/performance`) only counts shipped; learning loops read both.
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--red-soft)', marginBottom: 8 }}>Error: {error}</div>}
      {data !== null && !error && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 540 }}>
            <thead>
              <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Exchange</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Total</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Scored</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Pending</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Shipped (all/7d)</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'right' }}>Shadow (all/7d)</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Last scored</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const lastScored = r.last_scored_at ? new Date(r.last_scored_at).toLocaleString() : '—';
                return (
                  <tr key={r.exchange}>
                    <td style={{ padding: '6px 8px', color: 'var(--text-primary)', fontWeight: 500 }}>{r.exchange}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{r.total}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--green)' }}>{r.scored}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{r.pending}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--accent-soft)' }}>
                      {r.shipped_total} <span style={{ color: 'var(--text-muted)' }}>/ {r.shipped_last_7d}</span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--purple)' }}>
                      {r.shadow_total} <span style={{ color: 'var(--text-muted)' }}>/ {r.shadow_last_7d}</span>
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{lastScored}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', color: 'var(--text-faint)' }}>
                  No outcomes recorded yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── FlowFeedPanel ──────────────────────────────────────────────────────────
// Generic "type a ticker, hit Lookup, see the last N rows from a daily-flow
// table" component. Both the FINRA short-volume and SEC Form 4 panels share
// this shape — only the column definitions and the API client differ. Each
// also gets a "Trigger backfill now" button that kicks the corresponding
// daily job out-of-band (useful right after a fresh deploy or after an
// outage where the source was 429'ing).
//
// Surfaces:
//   • Latest fetched date across the WHOLE table (header line).
//   • Loading state for ticker lookups.
//   • Row count + a tabular view of the rows returned.
//   • Helpful empty-state ("No rows for AAPL — backfill may still be
//     running, try again in a minute").
//   • The triggerJob button is disabled while a job run is in flight to
//     avoid stacking simultaneous backfills.

function FlowFeedPanel({
  title,           // visible header
  subtitle,        // tiny clarifier under the header
  icon: IconC,     // lucide icon component
  fetchFn,         // (ticker) => Promise<{ rows, latestDate / latestFilingDate, count }>
  triggerFn,       // () => Promise — kicks the daily job
  latestKey,       // 'latestDate' (FINRA) | 'latestFilingDate' (SEC)
  columns,         // [{ key, label, render?: row => ReactNode }]
  rowKey,          // (row, idx) => string  for React keys
  emptyHint,       // shown when count === 0 for a specific ticker
}) {
  const [ticker, setTicker]     = useState('');
  const [data, setData]         = useState(null);     // last successful fetch payload
  const [loading, setLoading]   = useState(false);
  const [running, setRunning]   = useState(false);
  const [error, setError]       = useState(null);
  const [info, setInfo]         = useState(null);

  // On mount, fetch the global summary (no ticker) so the user sees the
  // table's latest date even before they search.
  useEffect(() => {
    let cancelled = false;
    fetchFn('').then(d => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [fetchFn]);

  const lookup = async (e) => {
    e?.preventDefault?.();
    setError(null); setInfo(null);
    const t = ticker.trim().toUpperCase();
    if (!t) {
      setError('Enter a ticker symbol.');
      return;
    }
    setLoading(true);
    try {
      const d = await fetchFn(t);
      setData(d);
    } catch (e2) {
      setError(e2.message);
    }
    setLoading(false);
  };

  const trigger = async () => {
    setError(null); setInfo(null);
    setRunning(true);
    try {
      const d = await triggerFn();
      setInfo(d?.message || 'Job triggered.');
      // Refresh the summary so the latestDate updates if the run succeeded.
      const summary = await fetchFn(ticker.trim().toUpperCase());
      setData(summary);
    } catch (e2) {
      setError(e2.message);
    }
    setRunning(false);
  };

  const latestDate = data?.[latestKey] || null;
  const rows       = Array.isArray(data?.rows) ? data.rows : [];

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {IconC && <IconC size={14} color="var(--accent-soft)" />}
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
        {subtitle && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {subtitle}</span>}
        <span style={{ flex: 1 }} />
        <span style={{
          fontSize: 11, color: latestDate ? 'var(--green)' : 'var(--amber)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          Latest in DB: {latestDate || 'none yet'}
        </span>
      </div>

      <form onSubmit={lookup} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder="Ticker — e.g. AAPL"
          style={{
            padding: '7px 12px', borderRadius: 'var(--r-sm)',
            border: '1px solid var(--border)', background: 'var(--bg-elevated)',
            color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit',
            outline: 'none', minWidth: 140,
          }}
        />
        <Button type="submit" variant="secondary" size="sm" disabled={loading || !ticker.trim()}>
          {loading ? 'Loading…' : 'Lookup'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={trigger} disabled={running}>
          {running ? 'Running…' : 'Trigger backfill now'}
        </Button>
      </form>

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--r-sm)', background: 'var(--red-dim)', color: 'var(--red-soft)', fontSize: 12, marginBottom: 10 }}>
          {error}
        </div>
      )}
      {info && !error && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--r-sm)', background: 'var(--green-dim)', color: 'var(--green-soft)', fontSize: 12, marginBottom: 10 }}>
          {info}
        </div>
      )}

      {data && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
          {data.ticker
            ? <>Returned <b>{rows.length}</b> row{rows.length === 1 ? '' : 's'} for <span style={{ color: 'var(--text-primary)' }}>{data.ticker}</span></>
            : <>Pass a ticker above to inspect per-symbol history.</>}
        </div>
      )}

      {data && data.ticker && rows.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px 0' }}>
          {emptyHint || 'No rows found.'}
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ maxHeight: 280, overflow: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-subtle)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9.5 }}>
                {columns.map((c) => (
                  <th key={c.key} style={{ textAlign: 'left', padding: '7px 10px', whiteSpace: 'nowrap' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={rowKey(row, i)} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  {columns.map((c) => (
                    <td key={c.key} style={{ padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ShortFlowPanel() {
  return (
    <FlowFeedPanel
      title="FINRA short flow — Reg SHO daily file"
      subtitle="cdn.finra.org · per-ticker short volume vs total"
      icon={Activity}
      fetchFn={apiGetShortFlow}
      triggerFn={apiRunShortFlowJob}
      latestKey="latestDate"
      emptyHint="No FINRA rows yet for this ticker — the daily backfill may still be running, or this ticker doesn't trade on FINRA-reported venues."
      rowKey={(r) => `${r.ticker || ''}-${r.date}`}
      columns={[
        { key: 'date',         label: 'Date',        render: (r) => String(r.date).slice(0, 10) },
        { key: 'short_volume', label: 'Short vol',   render: (r) => Number(r.short_volume).toLocaleString() },
        { key: 'total_volume', label: 'Total vol',   render: (r) => Number(r.total_volume).toLocaleString() },
        { key: 'short_ratio',  label: 'Short / total', render: (r) => `${(Number(r.short_ratio) * 100).toFixed(1)}%` },
        { key: 'market',       label: 'Market',      render: (r) => r.market || '—' },
      ]}
    />
  );
}

function InsiderFlowPanel() {
  return (
    <FlowFeedPanel
      title="SEC Form 4 — insider transactions"
      subtitle="sec.gov · open-market buys + sells from the daily index"
      icon={Search}
      fetchFn={apiGetInsiderFlow}
      triggerFn={apiRunInsiderFlowJob}
      latestKey="latestFilingDate"
      emptyHint="No Form 4 rows yet for this ticker — the daily backfill may still be running, or this ticker had no insider activity in the trailing window."
      rowKey={(r, i) => `${r.transaction_date}-${r.insider_name || i}`}
      columns={[
        { key: 'transaction_date', label: 'Date',     render: (r) => String(r.transaction_date).slice(0, 10) },
        { key: 'transaction_code', label: 'Code',     render: (r) => {
          const code = r.transaction_code;
          const colour = code === 'P' ? 'var(--green)' : code === 'S' ? 'var(--red)' : 'var(--text-muted)';
          return <span style={{ color: colour, fontWeight: 700 }}>{code}</span>;
        } },
        { key: 'insider_name',     label: 'Insider',  render: (r) => r.insider_name || '—' },
        { key: 'insider_title',    label: 'Title',    render: (r) => r.insider_title || '—' },
        { key: 'shares',           label: 'Shares',   render: (r) => Number(r.shares).toLocaleString() },
        { key: 'price',            label: 'Price',    render: (r) => r.price ? `$${Number(r.price).toFixed(2)}` : '—' },
        { key: 'value_usd',        label: 'Value',    render: (r) => r.value_usd ? `$${Number(r.value_usd).toLocaleString()}` : '—' },
      ]}
    />
  );
}

// ── OpenTrackedSignalsPanel ──────────────────────────────────────────────────
// Live view of the signals the Track Record is currently HOLDING — every
// gate-passing candidate (shipped + shadow) whose swing window hasn't elapsed
// yet — marked to market against live prices. Answers "what's open by the
// tracker, how's it doing right now, when did it open, how long left."
//
// This is the diagnostic that separates "the signal model" from "your
// trades": these are the signals the model is tracking, independent of what
// the user actually paper-traded.
function OpenTrackedSignalsPanel() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [shippedOnly, setShippedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await apiGetTrackedSignals({ shippedOnly, limit: 200 });
      setData(d);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [shippedOnly]);

  useEffect(() => { load(); }, [load]);

  const signals = data?.signals || [];
  const fmtAge = (h) => {
    if (h == null) return '—';
    if (h < 1)  return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
  };

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Target size={14} color="var(--accent-soft)" />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Open tracked signals</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· live P&amp;L of signals still inside their swing window</span>
        <span style={{ flex: 1 }} />
        {data && (
          <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-secondary)' }}>
            {data.count} open{data.avgReturnPct != null && <> · avg <b style={{ color: data.avgReturnPct >= 0 ? 'var(--green)' : 'var(--red)' }}>{data.avgReturnPct >= 0 ? '+' : ''}{data.avgReturnPct}%</b></>}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          onClick={() => setShippedOnly(v => !v)}
          style={{
            padding: '5px 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
            fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
            border: '1px solid var(--border)',
            background: shippedOnly ? 'var(--accent-dim)' : 'transparent',
            color: shippedOnly ? 'var(--accent-soft)' : 'var(--text-muted)',
          }}
        >
          {shippedOnly ? 'Shipped picks only' : 'All candidates (incl. shadow)'}
        </button>
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--r-sm)', background: 'var(--red-dim)', color: 'var(--red-soft)', fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}

      {!error && signals.length === 0 && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px 0' }}>
          No signals currently open — every tracked signal has already run its swing window and been scored.
        </div>
      )}

      {signals.length > 0 && (
        <div style={{ maxHeight: 380, overflow: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-subtle)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9.5, position: 'sticky', top: 0 }}>
                {['Ticker', 'Dir', 'Entry', 'Now', 'Return', 'Status', 'Conf', 'Opened', 'Left', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signals.map((s) => {
                const up = (s.returnPct ?? 0) >= 0;
                const statusColor = s.status === 'at/through target' ? 'var(--green)'
                                  : s.status === 'at/through stop'   ? 'var(--red)'
                                  : 'var(--text-muted)';
                return (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--border-subtle)', fontFamily: "'JetBrains Mono', monospace" }}>
                    <td style={{ padding: '6px 10px', fontWeight: 700, color: 'var(--text-primary)' }}>{s.ticker}</td>
                    <td style={{ padding: '6px 10px', color: s.direction === 'LONG' ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>{s.direction}</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>${s.entryPrice?.toFixed(2)}</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{s.currentPrice != null ? `$${s.currentPrice.toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '6px 10px', fontWeight: 700, color: s.returnPct == null ? 'var(--text-muted)' : up ? 'var(--green)' : 'var(--red)' }}>
                      {s.returnPct == null ? '—' : `${up ? '+' : ''}${s.returnPct}%`}
                    </td>
                    <td style={{ padding: '6px 10px', color: statusColor }}>{s.status}</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{s.confidence != null ? `${s.confidence}%` : '—'}</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }} title={new Date(s.createdAt).toLocaleString()}>{fmtAge(s.hoursOpen)} ago</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{s.hoursRemaining != null && s.hoursRemaining > 0 ? fmtAge(s.hoursRemaining) : 'scoring'}</td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-faint)', fontSize: 9 }}>{s.shipped ? '' : 'shadow'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── PerformanceTrendPanel ────────────────────────────────────────────────────
// Non-overlapping WEEKLY win-rate + return, so "is the signal win rate
// actually declining over time?" is answerable directly. The Track Record
// card's 15/30/90-day windows are nested (each contains the shorter ones) and
// so CANNOT show a trend — a small recent sample regressing toward the true
// mean looks like a "drop" when it's just statistics. This shows each week as
// its own bucket with its own win rate, so a real trend (or its absence) is
// visible. Each week's sample size is shown because a 5-signal week's win
// rate is noise; only weeks with a decent count are trustworthy.
function PerformanceTrendPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await apiGetPerformanceTrend({ weeks: 12 })); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const trend = data?.trend || [];
  const wrColor = (wr) => wr == null ? 'var(--text-muted)' : wr >= 55 ? 'var(--green)' : wr >= 45 ? 'var(--amber)' : 'var(--red)';

  // Objective trend verdict — two-proportion z-test on the earlier vs later
  // half of the weekly buckets (pooled wins/scored, sample-weighted). Turns
  // "I feel like the win rate dropped" into a statistical yes/no: if the
  // difference isn't beyond ~2 standard errors, it's noise, not a trend.
  const verdict = (() => {
    const weeks = trend.filter(w => w.scored > 0);
    if (weeks.length < 4) return null;
    const mid = Math.floor(weeks.length / 2);
    const early = weeks.slice(0, mid);
    const late  = weeks.slice(mid);
    const pool = (arr) => arr.reduce((a, w) => ({ wins: a.wins + w.wins, n: a.n + w.scored }), { wins: 0, n: 0 });
    const e = pool(early), l = pool(late);
    if (e.n < 10 || l.n < 10) return null;
    const p1 = e.wins / e.n, p2 = l.wins / l.n;
    const pPool = (e.wins + l.wins) / (e.n + l.n);
    const se = Math.sqrt(pPool * (1 - pPool) * (1 / e.n + 1 / l.n));
    if (se === 0) return null;
    const z = (p2 - p1) / se;                       // + = later half higher
    const diffPts = Math.round((p2 - p1) * 100);
    const sig = Math.abs(z) >= 1.96;                // 95%
    let label, color;
    if (!sig)           { label = 'Stable';    color = 'var(--text-secondary)'; }
    else if (z > 0)     { label = 'Improving';  color = 'var(--green)'; }
    else                { label = 'Declining';  color = 'var(--red)'; }
    return {
      label, color, diffPts, z: +z.toFixed(2), sig,
      detail: sig
        ? `Later weeks ${diffPts >= 0 ? '+' : ''}${diffPts}pts vs earlier — statistically significant (z=${z.toFixed(1)}).`
        : `Later weeks ${diffPts >= 0 ? '+' : ''}${diffPts}pts vs earlier — within noise (z=${z.toFixed(1)}, not significant). No real trend.`,
    };
  })();

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <Target size={14} color="var(--accent-soft)" />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Win-rate trend by week</span>
        {verdict && (
          <span
            title={verdict.detail}
            style={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em',
              padding: '3px 10px', borderRadius: 'var(--r-pill)', cursor: 'help',
              color: verdict.color,
              background: verdict.sig
                ? (verdict.z > 0 ? 'var(--green-dim)' : 'var(--red-dim)')
                : 'var(--bg-elevated)',
              border: `1px solid ${verdict.sig ? (verdict.z > 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)') : 'var(--border)'}`,
            }}
          >
            {verdict.label.toUpperCase()}
            {verdict.sig && <> · {verdict.diffPts >= 0 ? '+' : ''}{verdict.diffPts}pts</>}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={load} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </Button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Non-overlapping weekly buckets — unlike the nested 15/30/90d windows, this shows whether signals are actually trending better or worse over time. Trust the weeks with a larger sample; a low-count week's win rate is noise.
      </div>

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--r-sm)', background: 'var(--red-dim)', color: 'var(--red-soft)', fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}
      {!error && trend.length === 0 && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px 0' }}>Not enough scored signals yet to chart a weekly trend.</div>
      )}

      {trend.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {trend.map((w) => {
            const wr = w.winRatePct;
            const barPct = wr == null ? 0 : Math.max(2, Math.min(100, wr));
            const lowSample = w.scored < 8;
            return (
              <div key={w.weekStart} style={{ display: 'grid', gridTemplateColumns: '72px 1fr 96px', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 10.5, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-muted)' }}>
                  {w.weekStart.slice(5)}
                </span>
                <div style={{ position: 'relative', height: 18, borderRadius: 'var(--r-xs)', background: 'var(--bg-elevated)', overflow: 'hidden' }}
                  title={`${w.weekStart}: ${w.wins}W / ${w.losses}L · WR ${wr ?? '—'}% · avg ${w.avgReturnPct >= 0 ? '+' : ''}${w.avgReturnPct}% · LONG ${w.longWinRatePct ?? '—'}% / SHORT ${w.shortWinRatePct ?? '—'}%`}>
                  {/* 50% reference line */}
                  <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'var(--border-strong)' }} />
                  <div style={{ height: '100%', width: `${barPct}%`, background: wrColor(wr), opacity: lowSample ? 0.4 : 0.85, transition: 'width 0.3s' }} />
                  <span style={{ position: 'absolute', left: 8, top: 0, bottom: 0, display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-primary)' }}>
                    {wr != null ? `${wr}%` : '—'}
                  </span>
                </div>
                <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: lowSample ? 'var(--text-faint)' : 'var(--text-secondary)', textAlign: 'right' }}>
                  {w.scored}n{lowSample ? ' (thin)' : ''}
                  {w.avgReturnPct != null && <span style={{ color: w.avgReturnPct >= 0 ? 'var(--green)' : 'var(--red)', marginLeft: 6 }}>{w.avgReturnPct >= 0 ? '+' : ''}{w.avgReturnPct}%</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Per-factor attribution — the finer-grained self-learning layer. Shows which
// individual factors (RSI, MACD, short-flow, insider cluster, …) actually make
// money net of cost, learned nightly from scored outcomes. Cluster-level
// learning is coarse; this isolates each factor's realized edge and the bounded
// confidence nudge it earns. Read-only unless FACTOR_ADAPTIVE_ENABLED is set.
function FactorAttributionPanel({ onMessage }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState(null);
  const [win, setWin]         = useState(90);
  const [dir, setDir]         = useState('all');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await apiGetFactorAttribution()); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const recompute = useCallback(async () => {
    setRunning(true); setError(null);
    try {
      const d = await apiRunFactorAttribution();
      setData(d);
      onMessage?.('Factor attribution recomputed');
    } catch (e) { setError(e.message); }
    setRunning(false);
  }, [onMessage]);

  const meta    = data?.meta || {};
  const applied = data?.applied;
  const rows    = (data?.factors || [])
    .filter(r => r.window_days === win && (r.direction || 'all') === dir)
    .sort((a, b) => (b.weight - a.weight) || (b.n - a.n));

  const liftColor = (l) => l == null ? 'var(--text-muted)' : l > 1 ? 'var(--green)' : l < -1 ? 'var(--red)' : 'var(--text-secondary)';
  const chip = (active, label, onClick) => (
    <button onClick={onClick} style={{
      fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
      color: active ? 'var(--accent-soft)' : 'var(--text-muted)',
      background: active ? 'var(--accent-dim)' : 'var(--bg-elevated)',
      border: `1px solid ${active ? 'rgba(96,165,250,0.35)' : 'var(--border)'}`,
    }}>{label}</button>
  );

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <Target size={14} color="var(--accent-soft)" />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Per-factor realized edge</span>
        <span
          title={applied
            ? 'FACTOR_ADAPTIVE_ENABLED is ON — these nudges are applied to live signal confidence.'
            : 'Shadow mode — the edge is measured nightly but does NOT yet touch live scoring. Set FACTOR_ADAPTIVE_ENABLED=true to activate once the samples look meaningful.'}
          style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.03em', padding: '3px 10px', borderRadius: 'var(--r-pill)', cursor: 'help',
            color: applied ? 'var(--green)' : 'var(--text-secondary)',
            background: applied ? 'var(--green-dim)' : 'var(--bg-elevated)',
            border: `1px solid ${applied ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
          }}>
          {applied ? 'LIVE' : 'SHADOW'}
        </span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={recompute} disabled={running || loading}>
          {running ? 'Computing…' : 'Recompute now'}
        </Button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Marginal (univariate) attribution: for every scored signal that carried a factor, did it win net of cost? Win rates are Bayesian-shrunk toward the base rate and a factor needs ≥25 outcomes before it earns a non-zero <b>nudge</b>. Factors co-occur, so read this as a ranking of realized outcomes, not isolated causation.
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {chip(win === 90, '90d', () => setWin(90))}
        {chip(win === 180, '180d', () => setWin(180))}
        <span style={{ width: 8 }} />
        {chip(dir === 'all', 'All', () => setDir('all'))}
        {chip(dir === 'LONG', 'Long', () => setDir('LONG'))}
        {chip(dir === 'SHORT', 'Short', () => setDir('SHORT'))}
        {data?.factors?.length > 0 && (
          <span style={{ fontSize: 10.5, color: 'var(--text-faint)', alignSelf: 'center', marginLeft: 'auto' }}>
            base rate {rows[0]?.base_rate_pct != null ? `${rows[0].base_rate_pct}%` : '—'}
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--r-sm)', background: 'var(--red-dim)', color: 'var(--red-soft)', fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}
      {!error && rows.length === 0 && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px 0' }}>
          No scored outcomes in this window/direction yet. The nightly job populates this as signals mature past their swing window.
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 44px 62px 62px 58px 52px', gap: 8, fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.04em', textTransform: 'uppercase', paddingBottom: 2 }}>
            <span>Factor</span><span style={{ textAlign: 'right' }}>n</span><span style={{ textAlign: 'right' }}>Raw WR</span><span style={{ textAlign: 'right' }}>Shrunk</span><span style={{ textAlign: 'right' }}>Avg net</span><span style={{ textAlign: 'right' }}>Nudge</span>
          </div>
          {rows.map((r) => {
            const m = meta[r.factor] || {};
            const thin = r.n < 25;
            return (
              <div key={`${r.factor}-${r.direction}-${r.window_days}`}
                title={`${m.label || r.factor} · cluster: ${m.cluster || '—'}\nlift vs base: ${r.lift_pts >= 0 ? '+' : ''}${r.lift_pts}pts\nexpectancy: ${r.expectancy_pct >= 0 ? '+' : ''}${r.expectancy_pct}% per signal${thin ? '\n(below 25-sample gate → nudge held at 0)' : ''}`}
                style={{ display: 'grid', gridTemplateColumns: '1.6fr 44px 62px 62px 58px 52px', gap: 8, alignItems: 'center', padding: '6px 8px', borderRadius: 'var(--r-xs)', background: 'var(--bg-elevated)' }}>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label || r.factor}</span>
                  <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>{m.cluster || '—'}</span>
                </span>
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', color: thin ? 'var(--text-faint)' : 'var(--text-secondary)' }}>{r.n}</span>
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', color: 'var(--text-muted)' }}>{r.win_rate_pct}%</span>
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', color: liftColor(r.lift_pts) }}>{r.shrunk_wr_pct}%</span>
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', color: r.avg_net_return >= 0 ? 'var(--green)' : 'var(--red)' }}>{r.avg_net_return >= 0 ? '+' : ''}{r.avg_net_return}%</span>
                <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', color: r.weight > 0 ? 'var(--green)' : r.weight < 0 ? 'var(--red)' : 'var(--text-faint)' }}>
                  {r.weight > 0 ? '+' : ''}{r.weight}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [balances, setBalances] = useState([]);
  // Active tab — defaults to Approvals when there are pending users to
  // review, else Users (the most common admin task). Persists in
  // sessionStorage so a refresh while debugging the engine doesn't
  // dump you back at the top of the page.
  const [activeTab, setActiveTab] = useState(() => {
    try { return sessionStorage.getItem('admin.tab') || 'approvals'; }
    catch { return 'approvals'; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('admin.tab', activeTab); } catch { /* private mode */ }
  }, [activeTab]);
  const [editingBalance, setEditingBalance] = useState(null); // { id, exchange }
  const [newAmount, setNewAmount] = useState('');
  // Per-user extend panel state: { userId: { open, days, forever } }
  const [extendPanels, setExtendPanels] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [all, pend, bal] = await Promise.all([
        apiGetAllUsers(),
        apiGetPendingUsers().catch(() => ({ users: [] })),
        apiGetUserBalances().catch(() => ({ users: [] })),
      ]);
      setUsers(all);
      setPending(pend.users || []);
      setBalances(bal.users || []);
    } catch (e) {
      console.error('Failed to load admin data:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Kept as a thin shim so every call site stays unchanged; the underlying
  // surface is now the global toast instead of an inline banner.
  const showMsg = (text, ok = true) => {
    if (ok) toast.success(text);
    else    toast.error(text);
  };

  const handleApprove = async (u) => {
    try {
      const res = await apiApproveUser(u.id);
      showMsg(res.message);
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  const handleReject = async (u) => {
    try {
      const res = await apiRejectUser(u.id);
      showMsg(res.message);
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  const handleExtend = async (u) => {
    const panel = extendPanels[u.id] || {};
    try {
      const res = await apiExtendSubscription(u.id, { days: parseInt(panel.days) || undefined, forever: panel.forever });
      showMsg(res.message);
      setExtendPanels(p => ({ ...p, [u.id]: { open: false } }));
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  const handlePause = async (u) => {
    try {
      const res = await apiPauseSubscription(u.id);
      showMsg(res.message);
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  const handleResume = async (u) => {
    try {
      const res = await apiResumeSubscription(u.id);
      showMsg(res.message);
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  const handleCancel = async (u) => {
    if (!window.confirm(`Cancel subscription for ${u.name}? Their account will be kept but access will be blocked.`)) return;
    try {
      const res = await apiCancelSubscription(u.id);
      showMsg(res.message);
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete the account for ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      const res = await apiDeleteUser(u.id);
      showMsg(res.message);
      loadData();
    } catch (e) { showMsg(e.message, false); }
  };

  if (user?.role !== 'admin') {
    return <div style={{ padding: 40, color: 'var(--red)', textAlign: 'center' }}>Admin access only.</div>;
  }

  const nonAdminUsers = users.filter(u => u.role !== 'admin' && u.status !== 'pending');
  const trialCount = nonAdminUsers.filter(u => u.subscription_status === 'trial').length;
  const expiredCount = nonAdminUsers.filter(u => getEffectiveStatus(u) === 'expired').length;

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 'var(--r-md)',
              background: 'linear-gradient(135deg, rgba(91,106,240,0.20), rgba(91,106,240,0.06))',
              border: '1px solid rgba(91,106,240,0.30)',
            }}>
              <Shield size={17} color="var(--accent-soft)" strokeWidth={2.2} />
            </span>
            User Management
          </h1>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {pending.length === 0 && trialCount === 0 && expiredCount === 0 && (
              <Badge tone="green" size="sm" dot>All subscriptions up to date</Badge>
            )}
            {pending.length > 0 && (
              <Badge tone="amber" size="sm" dot="pulse">
                {pending.length} pending approval{pending.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {trialCount > 0 && (
              <Badge tone="accent" size="sm" dot>{trialCount} on trial</Badge>
            )}
            {expiredCount > 0 && (
              <Badge tone="red" size="sm" dot>{expiredCount} expired</Badge>
            )}
          </p>
        </div>
        <Button variant="ghost" size="md" icon={RefreshCw} onClick={loadData}>
          Refresh
        </Button>
      </div>

      {/* Action feedback now flows through the global toast system */}

      {/* Tab strip — wraps so it never overflows on phones */}
      <div className="filter-row" style={{ display: 'flex', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <Tabs
          ariaLabel="Admin sections"
          active={activeTab}
          onChange={setActiveTab}
          tabs={[
            { key: 'approvals', label: pending.length > 0 ? `Approvals (${pending.length})` : 'Approvals' },
            { key: 'users',     label: `Users (${nonAdminUsers.length})` },
            { key: 'engine',    label: 'Signal engine' },
            { key: 'ops',       label: 'Operations' },
          ]}
        />
      </div>

      {/* Pending approvals — tab: approvals */}
      {activeTab === 'approvals' && (
        pending.length > 0 ? (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--amber)', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={16} /> Pending Approvals ({pending.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pending.map(u => (
              <div key={u.id} className="pending-approval-row" style={{
                background: 'var(--bg-card)', borderRadius: 'var(--r-lg)', padding: '14px 18px',
                border: '1px solid rgba(245,158,11,0.22)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ minWidth: 0, flex: '1 1 240px' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{u.email}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
                    Signed up: {new Date(u.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="pending-approval-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button variant="success" size="md" icon={CheckCircle} onClick={() => handleApprove(u)}>
                    Approve &amp; Start Trial
                  </Button>
                  <Button variant="ghost" size="md" icon={XCircle} onClick={() => handleReject(u)}>
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        ) : (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            <CheckCircle size={28} color="var(--green)" style={{ marginBottom: 10 }} />
            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15, marginBottom: 4 }}>All caught up</div>
            <div>No users waiting for approval right now.</div>
          </div>
        )
      )}

      {/* Users table — tab: users */}
      {activeTab === 'users' && (<>
      {/* Users table */}
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Users size={16} color="var(--accent-soft)" /> All Users ({nonAdminUsers.length})
      </h2>
      <div className="admin-users-table" style={{
        background: 'var(--bg-card)', borderRadius: 'var(--r-lg)',
        border: '1px solid var(--border-subtle)', overflow: 'hidden', marginBottom: 24,
      }}>
        {/* Desktop header — hidden on mobile via CSS */}
        <div className="admin-users-header admin-table-header" style={{
          display: 'grid', gridTemplateColumns: '1.2fr 1.6fr 1fr 1.1fr 1.4fr', gap: 12,
          padding: '10px 18px', background: 'var(--bg-elevated)',
          fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          <span>Name</span><span>Email</span><span>Status</span><span>Expires</span><span>Actions</span>
        </div>

        {nonAdminUsers.map(u => {
          const eff = getEffectiveStatus(u);
          const isPaused = eff === 'paused';
          const isCancelled = eff === 'cancelled';
          const expiryDate = u.subscription_status === 'trial' ? u.trial_ends_at : u.subscription_ends_at;
          const days = daysUntil(expiryDate);
          const panel = extendPanels[u.id] || {};

          return (
            <div key={u.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <div className="admin-users-row" style={{
                display: 'grid', gridTemplateColumns: '1.2fr 1.6fr 1fr 1.1fr 1.4fr', gap: 12,
                padding: '12px 18px', fontSize: 13, alignItems: 'center',
              }}>
                <div className="cell" data-label="Name">
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{u.name}</span>
                </div>
                <div className="cell" data-label="Email" style={{ minWidth: 0 }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', wordBreak: 'break-all' }}>{u.email}</span>
                </div>
                <div className="cell" data-label="Status">
                  <StatusBadge user={u} />
                </div>
                <div className="cell" data-label="Expires" style={{ fontSize: 12 }}>
                  {eff === 'active' && !u.subscription_ends_at
                    ? <span style={{ color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}><Infinity size={12} /> Forever</span>
                    : expiryDate
                      ? <span style={{ color: days !== null && days <= 7 ? 'var(--amber)' : 'var(--text-secondary)' }}>
                          {fmtDate(expiryDate)}
                          {days !== null && days > 0 && ` (${days}d)`}
                          {days !== null && days <= 0 && ' (expired)'}
                        </span>
                      : <span style={{ color: 'var(--text-faint)' }}>—</span>
                  }
                </div>
                {/* Actions */}
                <div className="cell cell-actions" data-label="Actions">
                  <div className="admin-action-buttons" style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setExtendPanels(p => ({ ...p, [u.id]: { open: !panel.open, days: '30', forever: false } }))}
                    style={{ padding: '5px 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)', background: 'var(--accent-dim)', color: 'var(--accent-soft)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: `all var(--dur-fast) var(--ease-out)` }}>
                    Extend
                  </button>
                  {isPaused ? (
                    <button onClick={() => handleResume(u)} style={{ padding: '5px 12px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(34,197,94,0.35)', background: 'var(--green-dim)', color: 'var(--green)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Play size={10} /> Resume
                    </button>
                  ) : !isCancelled && (
                    <button onClick={() => handlePause(u)} style={{ padding: '5px 12px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(249,115,22,0.35)', background: 'rgba(249,115,22,0.10)', color: '#f97316', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Pause
                    </button>
                  )}
                  {!isCancelled && (
                    <button onClick={() => handleCancel(u)} style={{ padding: '5px 12px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(239,68,68,0.3)', background: 'var(--red-dim)', color: 'var(--red)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Cancel
                    </button>
                  )}
                  {isCancelled && (
                    <button onClick={() => handleResume(u)} style={{ padding: '5px 12px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(34,197,94,0.35)', background: 'var(--green-dim)', color: 'var(--green)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Play size={10} /> Restore
                    </button>
                  )}
                  <button onClick={() => handleDelete(u)} title="Delete account" style={{ padding: '5px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Trash2 size={12} />
                  </button>
                  </div>
                </div>
              </div>

              {/* Extend panel (spans all columns) */}
              {panel.open && (
                <div style={{
                  padding: '12px 18px 14px',
                  background: 'var(--accent-dim)',
                  borderTop: '1px solid var(--border-strong)',
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, width: '100%' }}>Extend for:</span>
                  {[30, 90, 180, 365].map(d => {
                    const active = String(panel.days) === String(d) && !panel.forever;
                    return (
                      <button key={d} onClick={() => setExtendPanels(p => ({ ...p, [u.id]: { ...panel, days: String(d), forever: false } }))}
                        style={{
                          padding: '6px 14px', borderRadius: 'var(--r-pill)',
                          border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                          background: active ? 'var(--grad-accent)' : 'var(--bg-elevated)',
                          color: active ? '#fff' : 'var(--text-secondary)',
                          fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                          boxShadow: active ? '0 2px 8px rgba(91,106,240,0.28)' : 'none',
                          transition: `all var(--dur-fast) var(--ease-out)`,
                        }}>
                        {d}d
                      </button>
                    );
                  })}
                  <button onClick={() => setExtendPanels(p => ({ ...p, [u.id]: { ...panel, forever: true, days: '' } }))}
                    style={{
                      padding: '6px 14px', borderRadius: 'var(--r-pill)',
                      border: `1px solid ${panel.forever ? 'var(--green)' : 'var(--border)'}`,
                      background: panel.forever ? 'var(--grad-success)' : 'var(--bg-elevated)',
                      color: panel.forever ? '#fff' : 'var(--text-secondary)',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 4,
                      boxShadow: panel.forever ? '0 2px 8px rgba(34,197,94,0.28)' : 'none',
                      transition: `all var(--dur-fast) var(--ease-out)`,
                    }}>
                    <Infinity size={11} /> Forever
                  </button>
                  <input
                    type="number" min="1" placeholder="Custom days"
                    value={panel.forever ? '' : panel.days}
                    onChange={e => setExtendPanels(p => ({ ...p, [u.id]: { ...panel, days: e.target.value, forever: false } }))}
                    style={{
                      width: 110, padding: '6px 10px', borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                      color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                    <button onClick={() => setExtendPanels(p => ({ ...p, [u.id]: { open: false } }))} style={{
                      padding: '6px 14px', borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--border)', background: 'transparent',
                      color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                    }}>Cancel</button>
                    <button onClick={() => handleExtend(u)} style={{
                      padding: '6px 16px', borderRadius: 'var(--r-sm)', border: 'none',
                      background: 'var(--grad-accent)', color: '#fff',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: '0 2px 8px rgba(91,106,240,0.28)',
                    }}>Confirm</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {nonAdminUsers.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
            {loading ? 'Loading...' : 'No users yet'}
          </div>
        )}
      </div>

      {/* Mock Money Management */}
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <DollarSign size={16} color="var(--green)" /> Mock Money Management
      </h2>
      <div className="admin-balance-table" style={{
        background: 'var(--bg-card)', borderRadius: 'var(--r-lg)',
        border: '1px solid var(--border-subtle)', overflow: 'hidden',
      }}>
        {/* Desktop header — hidden on mobile via CSS */}
        <div className="admin-table-header" style={{
          display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr',
          padding: '10px 18px', background: 'var(--bg-elevated)',
          fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', gap: 12,
        }}>
          <span>User / Email</span>
          <span>Cash balance (USD $)</span>
          <span>Realized P&amp;L</span>
        </div>

        {balances.map(u => {
          const isEditing = editingBalance?.id === u.id;
          const pnl = parseFloat(u.total_realized_pnl || 0);
          const pnlPositive = pnl >= 0;
          return (
            <div key={u.id} className="admin-balance-row" style={{
              display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 12,
              padding: '12px 18px', borderTop: '1px solid var(--border-subtle)', fontSize: 13, alignItems: 'start',
            }}>
              {/* User info */}
              <div className="cell" data-label="User" style={{ minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{u.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2, wordBreak: 'break-all' }}>{u.email}</div>
                </div>
              </div>

              {/* Cash balance */}
              <div className="cell" data-label="Cash balance">
                <div>
                  <div className="mono" style={{ color: '#38bdf8', fontWeight: 700, fontSize: 14 }}>
                    ${parseFloat(u.cash_balance || 10000).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ color: 'var(--text-faint)', fontSize: 10, marginTop: 1 }}>
                    {u.open_trades || 0} open trade{u.open_trades !== 1 ? 's' : ''}
                  </div>
                  {isEditing ? (
                    <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
                      <input value={newAmount} onChange={e => setNewAmount(e.target.value)}
                        type="number" placeholder="$" style={{
                          width: 90, padding: '5px 8px', borderRadius: 'var(--r-sm)', fontSize: 12,
                          border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                          color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none',
                        }} />
                      <button onClick={async () => {
                        if (!newAmount || parseFloat(newAmount) < 0) return;
                        try {
                          const res = await apiSetUserBalance(u.id, parseFloat(newAmount));
                          showMsg(res.message); setEditingBalance(null); loadData();
                        } catch (e) { showMsg(e.message, false); }
                      }} style={{
                        padding: '5px 12px', borderRadius: 'var(--r-sm)', border: 'none',
                        background: 'var(--grad-success)', color: '#fff', fontSize: 11, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit',
                        boxShadow: '0 2px 8px rgba(34,197,94,0.28)',
                      }}>Set</button>
                      <button onClick={() => setEditingBalance(null)} style={{
                        padding: '5px 10px', borderRadius: 'var(--r-sm)',
                        border: '1px solid var(--border)', background: 'transparent',
                        color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                      }}>×</button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingBalance({ id: u.id }); setNewAmount(parseFloat(u.cash_balance || 10000).toFixed(0)); }}
                      style={{
                        marginTop: 6, padding: '4px 12px', borderRadius: 'var(--r-pill)',
                        border: '1px solid rgba(14,165,233,0.35)',
                        background: 'rgba(14,165,233,0.10)',
                        color: '#38bdf8', fontSize: 10, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit',
                        transition: `all var(--dur-fast) var(--ease-out)`,
                      }}>
                      Edit $
                    </button>
                  )}
                </div>
              </div>

              {/* P&L summary */}
              <div className="cell" data-label="Realized P&amp;L">
                <div className="mono" style={{
                  fontSize: 15, fontWeight: 700,
                  color: pnlPositive ? 'var(--green)' : 'var(--red)',
                }}>
                  {pnlPositive ? '+' : ''}{pnl.toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
        {balances.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No users yet</div>
        )}
      </div>

      </>)}

      {/* Signal engine — tab: engine */}
      {activeTab === 'engine' && (<>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Target size={16} color="var(--accent-soft)" /> Signal engine
      </h2>
      {/* Aggregate Track Record — moved here from the Dashboard's Trust band.
          End users don't see signal performance numbers; the engine view
          lives with the other admin tooling for the people who actually
          tune it. 15d / 30d / 90d / 180d windows pinned on the server. */}
      <TrackRecordCard />
      {/* Weekly win-rate trend — non-overlapping buckets so an actual
          over-time direction is visible, vs the nested 15/30/90d windows
          that can't distinguish a real decline from small-sample regression. */}
      <div style={{ marginTop: 16 }}>
        <PerformanceTrendPanel />
      </div>
      {/* Live view of what the tracker is currently holding — the signals
          behind the Track Record numbers, marked to market right now. */}
      <div style={{ marginTop: 16 }}>
        <OpenTrackedSignalsPanel />
      </div>
      {/* External signal feeds — proof that the daily jobs are pulling data
          from FINRA + SEC EDGAR and the records are landing in the DB.
          Pick a ticker, hit Lookup, see the rows. "Trigger backfill now"
          kicks the corresponding daily job for catching up after an
          outage. */}
      <div style={{ marginTop: 16 }}>
        <ShortFlowPanel />
      </div>
      <div style={{ marginTop: 16 }}>
        <InsiderFlowPanel />
      </div>
      <div style={{ marginTop: 16 }}>
        <CalibrationCurvePanel onMessage={(text, ok) => (ok ? toast.success(text) : toast.error(text))} />
      </div>
      <div style={{ marginTop: 16 }}>
        <FactorAttributionPanel onMessage={(text) => toast.success(text)} />
      </div>
      <div style={{ marginTop: 16 }}>
        <OutcomesHealthPanel />
      </div>
      </>)}

      {/* Operations — tab: ops */}
      {activeTab === 'ops' && (<>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Database size={16} color="var(--accent-soft)" /> Operations
      </h2>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <DigestTrigger onMessage={(text, ok) => (ok ? toast.success(text) : toast.error(text))} />
      </div>
      <CacheStats />
      <div style={{ marginTop: 16 }}>
        <VixRegimePanel />
      </div>
      <div style={{ marginTop: 16 }}>
        <OptionQuoteInspector />
      </div>
      </>)}
    </div>
  );
}
